import { createContext, useContext, useId } from "react";
import type { ReactNode } from "react";
import { cx } from "./utils";

/** Aria wiring a Field hands down to its control. */
export interface FieldControlProps {
  id: string;
  "aria-describedby"?: string;
  "aria-invalid"?: boolean;
  required?: boolean;
}

const FieldCtx = createContext<FieldControlProps | null>(null);

/**
 * Read the enclosing Field's aria wiring. Input/Textarea/Select consume it
 * automatically; custom controls (Slider, SegmentedControl) can opt in.
 */
export function useFieldControl(): FieldControlProps | null {
  return useContext(FieldCtx);
}

export interface FieldProps {
  /** Visible label text. */
  label: ReactNode;
  /** Helper text under the control (`gz-help`). */
  hint?: ReactNode;
  /** Error text (`gz-error`); when set the control gets aria-invalid. */
  error?: ReactNode;
  /** Marks the control required and shows an asterisk. */
  required?: boolean;
  /** Render the label visually hidden (still announced). */
  hideLabel?: boolean;
  className?: string;
  /** The control; gets id/aria wiring through context. */
  children: ReactNode;
}

/** Design-system field (`gz-field`) that wires label, hint, error and aria ids. */
export function Field({
  label,
  hint,
  error,
  required,
  hideLabel,
  className,
  children,
}: FieldProps) {
  const base = useId();
  const id = `${base}-c`;
  const hintId = `${base}-h`;
  const errId = `${base}-e`;
  const describedBy = [hint != null ? hintId : null, error != null ? errId : null]
    .filter(Boolean)
    .join(" ");

  const ctx: FieldControlProps = {
    id,
    ...(describedBy ? { "aria-describedby": describedBy } : {}),
    ...(error != null ? { "aria-invalid": true } : {}),
    ...(required ? { required: true } : {}),
  };

  return (
    <div className={cx("gz-field", error != null && "is-invalid", className)}>
      <label className={cx(hideLabel && "xz-sr-only")} htmlFor={id}>
        {label}
        {required && (
          <span className="xz-field__req" aria-hidden="true">
            *
          </span>
        )}
      </label>
      <FieldCtx.Provider value={ctx}>{children}</FieldCtx.Provider>
      {hint != null && (
        <small className="gz-help" id={hintId}>
          {hint}
        </small>
      )}
      {error != null && (
        <small className="gz-error" id={errId} role="alert">
          {error}
        </small>
      )}
    </div>
  );
}
